import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useRef,
  useCallback,
  ReactNode,
} from 'react';
import axios from 'axios';
import i18n from '../i18n/config';

// Token refresh interval - refresh weekly (365d token)
const TOKEN_REFRESH_INTERVAL = 7 * 24 * 60 * 60 * 1000; // 7 days in milliseconds

interface User {
  id: string;
  email: string;
  name: string;
  isAdmin: boolean;
  language?: string;
  hasPassword?: boolean;
  isOAuthUser?: boolean;
}

interface AuthContextType {
  user: User | null;
  accessToken: string | null;
  isLoading: boolean;
  login: (email: string, password: string) => Promise<void>;
  signup: (
    email: string,
    password: string,
    name: string,
    dateOfBirth: string,
    language?: string,
    gender?: string,
  ) => Promise<{ userId: string }>;
  verifyEmail: (email: string, code: string) => Promise<void>;
  resendVerification: (email: string) => Promise<void>;
  logout: () => void;
  refreshToken: () => Promise<void>;
  setTokens: (accessToken: string, refreshToken: string) => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

// Configure axios defaults for path-based routing
// Frontend always uses /api prefix for API calls
// Nginx/ALB strips the /api prefix before forwarding to backend
axios.defaults.baseURL = process.env.REACT_APP_API_URL || 'http://localhost/api';

// Add request interceptor to include token
axios.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('accessToken');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error),
);

// Add response interceptor to handle token refresh
axios.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    // Don't retry auth endpoints or if already retried
    const isAuthEndpoint = originalRequest.url?.includes('/auth/');
    if (error.response?.status === 401 && !originalRequest._retry && !isAuthEndpoint) {
      originalRequest._retry = true;

      try {
        const refreshToken = localStorage.getItem('refreshToken');
        if (refreshToken) {
          const response = await axios.post(
            '/auth/refresh',
            {},
            {
              headers: { Authorization: `Bearer ${refreshToken}` },
            },
          );

          const { accessToken } = response.data;
          localStorage.setItem('accessToken', accessToken);

          originalRequest.headers.Authorization = `Bearer ${accessToken}`;
          return axios(originalRequest);
        }
      } catch (refreshError) {
        // Refresh failed, redirect to login
        localStorage.removeItem('accessToken');
        localStorage.removeItem('refreshToken');
        localStorage.removeItem('user');
        window.location.href = '/login';
      }
    }

    return Promise.reject(error);
  },
);

export const AuthProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [accessToken, setAccessToken] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const refreshTimerRef = useRef<NodeJS.Timeout | null>(null);
  const isRefreshingRef = useRef(false);

  // Proactive token refresh function
  const performTokenRefresh = useCallback(async () => {
    if (isRefreshingRef.current) return;

    const storedRefreshToken = localStorage.getItem('refreshToken');
    if (!storedRefreshToken) return;

    isRefreshingRef.current = true;
    try {
      const response = await axios.post(
        '/auth/refresh',
        {},
        {
          headers: { Authorization: `Bearer ${storedRefreshToken}` },
        },
      );

      const { accessToken: newAccessToken, refreshToken: newRefreshToken } = response.data;

      localStorage.setItem('accessToken', newAccessToken);
      localStorage.setItem('refreshToken', newRefreshToken);
      setAccessToken(newAccessToken);
    } catch (error) {
      // Refresh failed - token may be expired, let the 401 interceptor handle logout
      console.error('Proactive token refresh failed:', error);
    } finally {
      isRefreshingRef.current = false;
    }
  }, []);

  // Set up proactive refresh timer
  const startRefreshTimer = useCallback(() => {
    // Clear any existing timer
    if (refreshTimerRef.current) {
      clearInterval(refreshTimerRef.current);
    }

    // Start new timer for proactive refresh
    refreshTimerRef.current = setInterval(() => {
      performTokenRefresh();
    }, TOKEN_REFRESH_INTERVAL);
  }, [performTokenRefresh]);

  // Handle visibility change - refresh token when user returns to tab
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible' && localStorage.getItem('refreshToken')) {
        // User returned to tab - refresh token to ensure it's fresh
        performTokenRefresh();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [performTokenRefresh]);

  // Clean up timer on unmount
  useEffect(() => {
    return () => {
      if (refreshTimerRef.current) {
        clearInterval(refreshTimerRef.current);
      }
    };
  }, []);

  useEffect(() => {
    // Load user from localStorage on mount
    const storedUser = localStorage.getItem('user');
    const storedToken = localStorage.getItem('accessToken');

    if (storedUser && storedUser !== 'undefined' && storedToken && storedToken !== 'undefined') {
      try {
        const userData = JSON.parse(storedUser);
        setUser(userData);
        setAccessToken(storedToken);

        // Set the user's language preference in i18n
        if (userData.language) {
          i18n.changeLanguage(userData.language);
          localStorage.setItem('userLanguage', userData.language);
        }

        // Start proactive refresh timer when user is authenticated
        startRefreshTimer();

        // Sync timezone on app load
        try {
          const detectedTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
          if (detectedTimezone) {
            axios
              .put(
                '/users/timezone',
                { timezone: detectedTimezone },
                {
                  headers: { Authorization: `Bearer ${storedToken}` },
                },
              )
              .catch(() => {});
          }
        } catch {}
      } catch (error) {
        console.error('Failed to parse stored user data:', error);
        localStorage.removeItem('user');
        localStorage.removeItem('accessToken');
      }
    }

    setIsLoading(false);
  }, [startRefreshTimer]);

  const login = async (email: string, password: string) => {
    try {
      const response = await axios.post('/auth/login', { email, password });
      const { accessToken, refreshToken, user } = response.data;

      localStorage.setItem('accessToken', accessToken);
      localStorage.setItem('refreshToken', refreshToken);
      localStorage.setItem('user', JSON.stringify(user));

      setAccessToken(accessToken);
      setUser(user);

      // Start proactive refresh timer after login
      startRefreshTimer();

      // Set the user's language preference in i18n
      if (user.language) {
        i18n.changeLanguage(user.language);
        localStorage.setItem('userLanguage', user.language);
      }

      // Auto-detect and send timezone
      try {
        const detectedTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
        if (detectedTimezone) {
          axios
            .put(
              '/users/timezone',
              { timezone: detectedTimezone },
              {
                headers: { Authorization: `Bearer ${accessToken}` },
              },
            )
            .catch(() => {}); // Fire-and-forget
        }
      } catch {}

      // Check for pending SMS consent and persist it
      // Note: SMS consent only applies to users with phone numbers
      const pendingConsent = localStorage.getItem('chatnexus_pending_consent');
      if (pendingConsent && user.phoneNumber) {
        try {
          const consentData = JSON.parse(pendingConsent);
          // Only persist if the email matches (user logging into account they just created)
          if (consentData.email === email) {
            await axios.post(
              '/sms-consent/signup',
              {
                phoneNumber: user.phoneNumber,
              },
              {
                headers: {
                  Authorization: `Bearer ${accessToken}`,
                },
              },
            );
            // Clear pending consent after successful persistence
            localStorage.removeItem('chatnexus_pending_consent');
          }
        } catch (consentError) {
          console.error('Failed to persist SMS consent:', consentError);
          // Don't block login if consent fails to persist
          // Keep pending consent in localStorage for potential future use
        }
      }
    } catch (error: any) {
      throw new Error(error.response?.data?.message || 'Login failed');
    }
  };

  const signup = async (
    email: string,
    password: string,
    name: string,
    dateOfBirth: string,
    language?: string,
    gender?: string,
  ) => {
    try {
      const response = await axios.post('/auth/signup', {
        email,
        password,
        name,
        dateOfBirth,
        language,
        gender,
      });
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.message || 'Signup failed');
    }
  };

  const verifyEmail = async (email: string, code: string) => {
    try {
      const response = await axios.post('/auth/verify-email', { email, code });

      // If verification returns auth tokens, auto-login the user
      if (response.data?.accessToken) {
        const { accessToken, refreshToken, user } = response.data;

        localStorage.setItem('accessToken', accessToken);
        localStorage.setItem('refreshToken', refreshToken);
        localStorage.setItem('user', JSON.stringify(user));

        setAccessToken(accessToken);
        setUser(user);

        // Start proactive refresh timer after email verification login
        startRefreshTimer();

        // Set the user's language preference in i18n
        if (user.language) {
          i18n.changeLanguage(user.language);
          localStorage.setItem('userLanguage', user.language);
        }
      }
    } catch (error: any) {
      throw new Error(error.response?.data?.message || 'Verification failed');
    }
  };

  const resendVerification = async (email: string) => {
    try {
      await axios.post('/auth/resend-verification', { email });
    } catch (error: any) {
      throw new Error(error.response?.data?.message || 'Failed to resend verification');
    }
  };

  const logout = () => {
    // Clear refresh timer
    if (refreshTimerRef.current) {
      clearInterval(refreshTimerRef.current);
      refreshTimerRef.current = null;
    }

    localStorage.removeItem('accessToken');
    localStorage.removeItem('refreshToken');
    localStorage.removeItem('user');
    localStorage.removeItem('theme'); // Clear theme preference on logout
    localStorage.removeItem('token'); // Also clear the generic token if it exists
    setUser(null);
    setAccessToken(null);
  };

  const refreshToken = async () => {
    try {
      const refreshToken = localStorage.getItem('refreshToken');
      if (!refreshToken) throw new Error('No refresh token');

      const response = await axios.post(
        '/auth/refresh',
        {},
        {
          headers: { Authorization: `Bearer ${refreshToken}` },
        },
      );

      const { accessToken: newAccessToken, refreshToken: newRefreshToken } = response.data;

      localStorage.setItem('accessToken', newAccessToken);
      localStorage.setItem('refreshToken', newRefreshToken);
      setAccessToken(newAccessToken);
    } catch (error) {
      logout();
      throw error;
    }
  };

  const setTokens = (newAccessToken: string, newRefreshToken: string) => {
    localStorage.setItem('accessToken', newAccessToken);
    localStorage.setItem('refreshToken', newRefreshToken);
    setAccessToken(newAccessToken);

    // Start proactive refresh timer after OAuth login
    startRefreshTimer();

    // Decode the token to get user info
    try {
      const b64 = newAccessToken.split('.')[1].replace(/-/g, '+').replace(/_/g, '/');
      const payload = JSON.parse(atob(b64 + '='.repeat((4 - (b64.length % 4)) % 4)));
      const userData = {
        id: payload.sub,
        email: payload.email,
        name: payload.name,
        isAdmin: payload.isAdmin || false,
        language: payload.language || 'en',
      };
      setUser(userData);
      localStorage.setItem('user', JSON.stringify(userData));
    } catch (error) {
      console.error('Failed to decode token:', error);
    }
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        accessToken,
        isLoading,
        login,
        signup,
        verifyEmail,
        resendVerification,
        logout,
        refreshToken,
        setTokens,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
